/**
 * Earnings Calculator utilities.
 *
 * Show providers their estimated earnings after platform fees,
 * payment processing fees, and taxes.
 *
 * Fee structure (configurable):
 *   - Platform commission: % of gross transaction amount
 *   - Payment processing: 2.9% + 30¢ (Stripe default)
 *   - VAT on commission: % on the commission amount
 *
 * All amounts in cents.
 */

export const DEFAULT_PLATFORM_COMMISSION_PCT = 10;
export const DEFAULT_STRIPE_RATE = 0.029;
export const DEFAULT_STRIPE_FIXED = 30; // cents

/**
 * Calculate Stripe processing fee.
 * @param {number} grossCents
 * @param {number} [rate] — default 2.9%
 * @param {number} [fixed] — default 30 cents
 * @returns {number}
 */
export const stripeProcessingFee = (grossCents, rate = DEFAULT_STRIPE_RATE, fixed = DEFAULT_STRIPE_FIXED) =>
  Math.round(grossCents * rate) + fixed;

/**
 * Calculate full earnings breakdown.
 * @param {number} grossCents — total charged to customer
 * @param {Object} config
 * @param {number} [config.platformCommissionPct]
 * @param {number} [config.vatOnCommissionPct]
 * @param {number} [config.processingRate]
 * @param {number} [config.processingFixed]
 * @returns {Object} — full breakdown
 */
export const calculateEarnings = (grossCents, config = {}) => {
  const {
    platformCommissionPct = DEFAULT_PLATFORM_COMMISSION_PCT,
    vatOnCommissionPct = 0,
    processingRate = DEFAULT_STRIPE_RATE,
    processingFixed = DEFAULT_STRIPE_FIXED,
  } = config;

  const platformFeeCents = Math.round(grossCents * (platformCommissionPct / 100));
  const vatOnCommissionCents = Math.round(platformFeeCents * (vatOnCommissionPct / 100));
  const processingFeeCents = stripeProcessingFee(grossCents, processingRate, processingFixed);
  const totalFeesCents = platformFeeCents + vatOnCommissionCents + processingFeeCents;
  const netEarningsCents = grossCents - totalFeesCents;

  return {
    grossCents,
    platformFeeCents,
    vatOnCommissionCents,
    processingFeeCents,
    totalFeesCents,
    netEarningsCents,
    netEarningsPct: Math.round((netEarningsCents / grossCents) * 100),
  };
};

/**
 * Calculate required gross price to achieve a target net earning.
 * @param {number} targetNetCents
 * @param {Object} config
 * @returns {number} — required gross price in cents
 */
export const grossFromNet = (targetNetCents, config = {}) => {
  const { platformCommissionPct = DEFAULT_PLATFORM_COMMISSION_PCT, processingRate = DEFAULT_STRIPE_RATE, processingFixed = DEFAULT_STRIPE_FIXED } = config;
  // Solve: net = gross - gross*(commission%) - gross*(stripe%) - fixed
  // net + fixed = gross * (1 - commission% - stripe%)
  const rate = (platformCommissionPct / 100) + processingRate;
  return Math.ceil((targetNetCents + processingFixed) / (1 - rate));
};
